/**
 * @fileoverview Guided Filter 参数和验证模块
 * 定义算法参数类型、默认值和验证函数
 * 
 * @author 织
 * @version 1.0.0
 */

/**
 * Guided Filter 参数类型定义
 * @typedef {Object} GuidedFilterParams
 * @property {number} sigma - 高斯滤波标准差 [1.0, 16.0]
 * @property {number} epsilon - 正则化参数 [0.01, 0.1]
 * @property {number} radius - 窗口半径 [4, 32]
 * @property {number} blockSize - 线程组大小 [8, 32]
 * @property {boolean} enableOptimization - 启用优化
 */

/**
 * 默认Guided Filter参数
 * @type {GuidedFilterParams}
 */
export const DEFAULT_GUIDED_FILTER_PARAMS = {
  sigma: 8.0,
  epsilon: 0.04,
  radius: 8,
  blockSize: 16,
  enableOptimization: true
};

/**
 * 验证Guided Filter参数
 * @param {GuidedFilterParams} params - 输入参数
 * @returns {boolean} 参数是否有效
 */
export function validateGuidedFilterParams(params) {
  if (!params || typeof params !== 'object') {
    return false;
  }
  
  const { sigma, epsilon, radius, blockSize } = params;
  
  // 验证sigma
  if (typeof sigma !== 'number' || sigma < 1.0 || sigma > 16.0) {
    return false;
  }
  
  // 验证epsilon
  if (typeof epsilon !== 'number' || epsilon < 0.01 || epsilon > 0.1) {
    return false;
  }
  
  // 验证radius
  if (typeof radius !== 'number' || radius < 2 || radius > 32) {
    return false;
  }
  
  // 验证blockSize
  if (typeof blockSize !== 'number' || blockSize < 8 || blockSize > 32) {
    return false;
  }
  
  return true;
}

/**
 * 计算分离高斯滤波权重
 * 将2D高斯滤波分解为两个1D滤波以优化性能
 * @param {number} sigma - 高斯滤波标准差
 * @param {number} radius - 滤波半径
 * @returns {Float32Array} 归一化的权重数组
 */
export function computeSeparableFilterWeights(sigma, radius) {
  const weights = new Float32Array(radius * 2 + 1);
  let sum = 0.0;
  
  // 计算高斯权重
  for (let i = -radius; i <= radius; i++) {
    const weight = Math.exp(-(i * i) / (2 * sigma * sigma));
    weights[i + radius] = weight;
    sum += weight;
  }
  
  // 归一化权重
  for (let i = 0; i < weights.length; i++) {
    weights[i] /= sum;
  }
  
  return weights;
}

/**
 * 优化线程组大小
 * 基于纹理尺寸和GPU能力自动调整
 * @param {number} width - 纹理宽度
 * @param {number} height - 纹理高度
 * @param {Object} gpuInfo - GPU信息
 * @returns {{x: number, y: number}} 优化的线程组大小
 */
export function optimizeWorkgroupSize(width, height, gpuInfo) {
  // 基于纹理尺寸优化
  const optimalX = Math.min(32, Math.ceil(width / 64));
  const optimalY = Math.min(32, Math.ceil(height / 64));
  
  // 确保是2的幂次
  const x = Math.pow(2, Math.floor(Math.log2(optimalX)));
  const y = Math.pow(2, Math.floor(Math.log2(optimalY)));
  
  return { x, y };
}

/**
 * 获取共享内存布局
 * 使用共享内存缓存减少全局内存访问
 * @param {number} radius - 滤波半径
 * @returns {{cacheSize: number, tileSize: number}} 共享内存布局
 */
export function getSharedMemoryLayout(radius) {
  const tileSize = 16 + 2 * radius;
  const cacheSize = tileSize * tileSize * 4; // vec4<f32>
  
  return {
    cacheSize,
    tileSize
  };
} 